import type * as geojson from 'geojson';
import React from 'react';

import {ElevationProfile} from 'src/components/SWEAnalysis';
import {useTranslations} from 'src/components/TranslationsContext';
import WithWidth from 'src/components/WithWidth';
import {SWEAnalysisResponse} from 'src/lib/api/analysis';
import {Site} from 'src/lib/api/project';
import {RegionalReportResponse, SiteSynopsis} from 'src/lib/api/regionalReport';

import {percentColor} from './colors';
import {generateSWENarrative, generateSiteNarrative, showSWEPercentages} from './commentary';
import NdsiChart from './NdsiChart';
import SiteForecastViz from './SiteForecastViz';
import SiteLocatorMap from './SiteLocatorMap';

const EXCEEDANCE_COLUMNS = [
  {quantile: 'discharge_q0.1', label: '90%'},
  {quantile: 'discharge_q0.25', label: '75%'},
  {quantile: 'discharge_q0.5', label: '50%'},
  {quantile: 'discharge_q0.75', label: '25%'},
  {quantile: 'discharge_q0.9', label: '10%'},
];

function SiteExceedanceTable({
  siteSynopsis,
  targetPeriods,
}: {
  siteSynopsis: Record<string, SiteSynopsis>;
  targetPeriods: RegionalReportResponse['targetPeriods'];
}) {
  return (
    <table className="text-xs w-full">
      <thead>
        <tr className="border-b border-gray-300">
          <th className="text-left py-1 px-2 font-semibold">Period</th>
          {EXCEEDANCE_COLUMNS.map((col) => (
            <th key={col.quantile} className="text-right py-1 px-2 font-semibold text-gray-600">
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {targetPeriods.map((tp) => {
          const tpData = siteSynopsis[tp.label];
          return (
            <tr key={tp.label} className="border-b border-gray-100">
              <td className="py-1 px-2 text-gray-700">{tp.label}</td>
              {EXCEEDANCE_COLUMNS.map((col) => {
                const val = tpData?.percentOfNormal?.[col.quantile] ?? null;
                return (
                  <td
                    key={col.quantile}
                    className={`py-1 px-2 text-right tabular-nums ${percentColor(val)}`}
                  >
                    {val !== null ? Math.round(val) : '—'}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

type SweEntry =
  | (SWEAnalysisResponse & {dataSource?: string})
  | {dataSource: 'unavailable'; reason: string; spatialCoverage?: number};

type Props = {
  site: {siteId: string; name: string};
  fullSite: Site | undefined;
  siteSynopsis: Record<string, SiteSynopsis>;
  targetPeriods: RegionalReportResponse['targetPeriods'];
  forecastYear: number;
  reportDate: string;
  dateRange: [Date, Date];
  sweData: SweEntry | undefined;
  allSites: RegionalReportResponse['sites'];
  basinOutline: geojson.Geometry | null;
  snapshotDate?: string;
};

function isSweAvailable(e: SweEntry | undefined): e is SWEAnalysisResponse & {dataSource?: string} {
  return !!e && e.dataSource !== 'unavailable' && 'elevationProfile' in e;
}

export default function SiteDetailSection({
  site,
  fullSite,
  siteSynopsis,
  targetPeriods,
  forecastYear,
  reportDate,
  dateRange,
  sweData,
  allSites,
  basinOutline,
  snapshotDate,
}: Props) {
  const {t} = useTranslations();
  const narrative = generateSiteNarrative(site.name, siteSynopsis, targetPeriods, forecastYear);
  const sweAvailable = isSweAvailable(sweData);
  const sweNarrative = sweAvailable
    ? generateSWENarrative(site.name, sweData, reportDate, t)
    : null;
  const sweUnavailableReason =
    sweData && sweData.dataSource === 'unavailable' && 'reason' in sweData ? sweData.reason : null;

  return (
    <div
      data-regional-report-site-id={site.siteId}
      id={`site-${site.siteId}`}
      className="break-inside-avoid mb-8 scroll-mt-8"
    >
      <div className="flex items-start gap-3 border-b border-gray-200 pb-1 mb-1">
        <h3 className="text-base font-bold flex-1 self-center">{site.name}</h3>
        <SiteLocatorMap sites={allSites} activeSiteId={site.siteId} basinOutline={basinOutline} />
      </div>
      {narrative && <p className="text-sm text-gray-700 leading-relaxed my-2">{narrative}</p>}

      {/* Full-width stacked sections — easier to scan than a 2-column grid
          when each subsection has different natural heights. */}
      <div className="mt-3 space-y-5">
        <div>
          <h4 className="text-xs font-semibold mb-1 text-gray-500 uppercase tracking-wide">
            Forecast Volumes (% of Normal)
          </h4>
          <SiteExceedanceTable siteSynopsis={siteSynopsis} targetPeriods={targetPeriods} />
        </div>

        {fullSite ? (
          <div className="break-inside-avoid">
            <h4 className="text-xs font-semibold mb-1 text-gray-500 uppercase tracking-wide">
              Forecast vs Long-Term Average
            </h4>
            <SiteForecastViz site={fullSite} dateRange={dateRange} />
          </div>
        ) : (
          <div className="text-xs text-gray-400">Forecast data not available for this site.</div>
        )}

        {(sweNarrative || sweUnavailableReason || sweAvailable || snapshotDate) && (
          <div>
            <h4 className="text-xs font-semibold mb-1 text-gray-500 uppercase tracking-wide">
              Basin Conditions — Snow Water Equivalent
            </h4>
            {sweNarrative && (
              <p className="text-sm text-gray-700 leading-relaxed my-1">{sweNarrative}</p>
            )}
            {sweUnavailableReason && (
              <p className="text-xs text-gray-400 italic my-1">
                SWE unavailable: {sweUnavailableReason}
              </p>
            )}
            {/* SWANN-derived elevation profile is the canonical basin-wide SWE
                view — SWANN's server-side path tolerates partial coverage
                (via _compute_swann_overlap + n_bands). The SNODAS time-series
                chart is still skipped because basins that extend into Canada
                (e.g. The Dalles, Priest Rapids) trigger a zarr assertion when
                their contributing HRUs don't all have SNODAS Series records. */}
            {sweAvailable && (
              <div className="mt-4 break-inside-avoid" data-regional-report-elevation>
                <WithWidth>
                  {(width) => (
                    <ElevationProfile
                      data={sweData.elevationProfile}
                      width={width}
                      volumeUnit={sweData.volumeUnit}
                      elevationUnit={sweData.elevationUnit}
                      siteName={site.name}
                      height={320}
                      showPercentOfAverage={showSWEPercentages(reportDate)}
                    />
                  )}
                </WithWidth>
              </div>
            )}
            {/* MODIS NDSI is global, so basin-averaging works even for
                cross-border basins — safe to include. */}
            {fullSite && (
              <div className="mt-4 break-inside-avoid">
                <NdsiChart site={fullSite} endDate={snapshotDate} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
